import {
  MAX_TEXT_MESSAGE_LENGTH,
  MessageDto,
  MessageForm,
  MessageFormSchema,
  MustacheTemplateVariablesDto,
  UserDto,
} from '@act/shared/data-transfer-objects';
import { zodResolver } from '@hookform/resolvers/zod';
import SmsIcon from '@mui/icons-material/Sms';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { visitDateTimeHelpers } from 'utilities/dateTimeGenerator.util';
import { applyMustacheTemplateWithValidation } from 'utilities/formatters/mustacheTemplateFormatter';
import useConfirmationAlertDialog from '../../hooks/useConfirmationAlertDialog.hook';
import useUnsavedChangesDialog from '../../hooks/useUnsavedChangesDialog.hook';
import {
  buildPhoneNumberOptions,
  isDoNotContactSelected,
  NO_PHONE_NUMBER_ID,
} from '../messageDialog.util';
import { useMessageAttachments } from './useMessageAttachments.hook';
import { useMessageSubmission } from './useMessageSubmission.hook';

interface UseMessageDialogInput {
  user: UserDto;
  message?: MessageDto;
  scheduledEventId?: number;
  handleClose?: () => void;
  setOpen?: (open: boolean) => void;
  callback?: (success: boolean) => void;
}

export interface ScheduleSelection {
  scheduleDate: Date;
  scheduleTime: number;
}

/**
 * Drives the text message dialog: form state, phone number options, attachment
 * uploads, and the send / schedule actions.
 * @param input - Member, optional message being edited, and dialog close wiring.
 * @returns Form bindings, derived send gates, and dialog action handlers.
 */
export const useMessageDialog = ({
  user,
  message,
  scheduledEventId = null,
  handleClose,
  setOpen,
  callback,
}: UseMessageDialogInput) => {
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const openConfirmationAlertDialog = useConfirmationAlertDialog();
  const attachments = useMessageAttachments(message?.fileIds);

  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleFormData, setScheduleFormData] = useState<MessageForm | null>(
    null
  );
  const [templateValidationError, setTemplateValidationError] = useState<
    string | null
  >(null);
  const [missingVariables, setMissingVariables] = useState<string[]>([]);

  const closeDialog = useCallback(
    (success = false) => {
      handleClose?.();
      setOpen?.(false);
      callback?.(success);
    },
    [callback, handleClose, setOpen]
  );

  const submission = useMessageSubmission({
    user,
    message,
    scheduledEventId,
    attachments,
    closeDialog,
  });

  const { control, handleSubmit, setValue, getValues, formState, trigger } =
    useForm<MessageForm>({
      defaultValues: {
        phoneNumberId:
          message?.phoneNumberId ??
          user?.phoneNumbers?.find(phone => phone.primary)?.id ??
          NO_PHONE_NUMBER_ID,
        message: message?.text ?? '',
      },
      resolver: zodResolver(MessageFormSchema),
    });

  const phoneNumberOptions = useMemo(
    () => buildPhoneNumberOptions(user?.phoneNumbers),
    [user?.phoneNumbers]
  );

  const hasNoPhoneNumbers =
    phoneNumberOptions.length === 1 &&
    phoneNumberOptions[0].value === NO_PHONE_NUMBER_ID;

  const selectedPhoneNumberId = useWatch({ control, name: 'phoneNumberId' });

  /*
   * The API rejects sends to a number on the do-not-contact list, so the
   * selection has to gate the dialog actions rather than surfacing as a
   * failure after submit.
   */
  const isSelectedNumberDoNotContact = isDoNotContactSelected(
    user?.phoneNumbers,
    selectedPhoneNumberId
  );

  useEffect(() => {
    if (hasNoPhoneNumbers && !message) {
      setValue('phoneNumberId', NO_PHONE_NUMBER_ID);
    }
  }, [hasNoPhoneNumbers, message, setValue]);

  const clearTemplateValidation = () => {
    setMissingVariables([]);
    setTemplateValidationError(null);
  };

  const formConfig: FormConfig<MessageForm> = useMemo(
    () => [
      {
        type: 'input',
        name: 'phoneNumberId',
        label: 'Phone Number',
        input: 'select',
        disabled: hasNoPhoneNumbers,
        trackingName: 'phone-number-select',
        props: {
          required: true,
          fullWidth: true,
          options: phoneNumberOptions,
        },
      },
      {
        type: 'input',
        name: 'message',
        label: 'Text Message',
        input: 'text',
        trackingName: 'message-text-input',
        props: {
          multiline: true,
          minRows: 4,
          characterLimit: MAX_TEXT_MESSAGE_LENGTH,
          placeholder: 'Enter your message...',
          onBlurCallback: clearTemplateValidation,
        },
      },
    ],
    [hasNoPhoneNumbers, phoneNumberOptions]
  );

  const handleApplyTemplate = (
    template: string,
    templateVariables: MustacheTemplateVariablesDto
  ) => {
    const validationResult = applyMustacheTemplateWithValidation(
      template,
      templateVariables
    );

    setValue('message', validationResult.renderedTemplate, {
      shouldDirty: true,
    });

    setMissingVariables(validationResult.missingVariables);
    setTemplateValidationError(
      validationResult.hasMissingVariables
        ? `Some template variables are missing: ${validationResult.missingVariables.join(
            ', '
          )}. The message may appear incomplete.`
        : null
    );
  };

  const confirmIncompleteTemplate = () =>
    openConfirmationAlertDialog({
      title: 'Confirm Message Send',
      icon: <SmsIcon />,
      iconColor: 'warning',
      iconBorderColor: 'warningContainer',
      avatarCaption: 'Warning',
      message:
        'This message contains template variables that may not be filled in properly. The message may appear incomplete to the recipient.',
      cancelText: 'Cancel',
      confirmationText: 'Send Anyway',
      confirmButtonColor: 'warning',
    });

  const handleSave = async (data: MessageForm) => {
    if (submission.isSending || attachments.isUploading) {
      return;
    }
    submission.clearError();

    if (missingVariables.length > 0 && !(await confirmIncompleteTemplate())) {
      return;
    }

    await submission.sendNow(data);
  };

  /* ScheduleMessageDialog types its callback loosely; only a selection is ever sent. */
  const handleScheduleMessage = (data: MessageForm | ScheduleSelection) => {
    if ('scheduleDate' in data && 'scheduleTime' in data) {
      void submission.scheduleSend(
        message ? getValues() : scheduleFormData,
        data
      );
    }
  };

  const closeForm = () => {
    if (formState.isDirty) {
      void handleUnsavedChanges(() => closeDialog());
      return;
    }
    closeDialog();
  };

  return {
    control,
    formState,
    formConfig,
    attachments,
    isSelectedNumberDoNotContact,
    isSubmitDisabled:
      hasNoPhoneNumbers ||
      isSelectedNumberDoNotContact ||
      !!templateValidationError,
    isSending: submission.isSending,
    isSaving: submission.isSaving,
    error: submission.error,
    clearError: submission.clearError,
    templateValidationError,
    dismissTemplateValidationError: () => setTemplateValidationError(null),
    handleApplyTemplate,
    isScheduleDialogOpen,
    closeScheduleDialog: () => {
      setIsScheduleDialogOpen(false);
      setScheduleFormData(null);
    },
    handleScheduleMessage,
    openScheduleDialog: () => {
      if (!formState.isValid) {
        void trigger();
        return;
      }
      setScheduleFormData(getValues());
      setIsScheduleDialogOpen(true);
    },
    handleCancel: () => {
      if (attachments.cancelUpload()) {
        return;
      }
      closeForm();
    },
    closeForm,
    submitForm: handleSubmit(data => {
      if (message?.sendAt) {
        void submission.scheduleSend(getValues(), {
          scheduleDate: new Date(message.sendAt),
          scheduleTime: visitDateTimeHelpers.getTimeValue(message.sendAt),
        });
        return;
      }
      void handleSave(data);
    }),
  };
};
